import {
  Input,
  Button,
  Typography
} from "@material-tailwind/react";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EyeIcon, EyeSlashIcon, UserCircleIcon } from "@heroicons/react/24/solid";
import Select from 'react-select';
import Toaster from "../../components/Toaster";
import { setUser } from "../../store/slices/userSlice";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import { doLogin, setLoginDetails, resetStatus } from "../../store/slices/loginSlice";
import { fetchCountryMobileCode } from "../../store/slices/internalUsersSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { requestFCMToken } from "../../firebase/requestFCMToken";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";
import { isDeliveryPartner } from "@/utils/userRoles";
import { useTranslation } from "react-i18next";
import { CheckIcon, LanguageIcon } from "@heroicons/react/24/outline";
import Header from "./header";

const loginSchema= (t) => Yup.object().shape({
  emailOrPhone: Yup.string()
    .required(t("validation.emailOrPhoneRequired"))
    .test(
      "email-or-phone",
      t("validation.invalidEmailOrPhone"),
      (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{8,15}$/;

        return emailRegex.test(value) || phoneRegex.test(value);
      }
    ),

  password: Yup.string().when("emailOrPhone", {
    is: (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
    then: (schema) =>
      schema.required(t("validation.passwordRequired")),
    otherwise: (schema) => schema.notRequired(),
  }),

  country_code: Yup.string().when("emailOrPhone", {
    is: (val) => /^[0-9]{8,15}$/.test(val),
    then: (schema) =>
      schema.required(t("validation.countryCodeRequired")),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t , i18n } = useTranslation("login");
  const [deviceToken, setDeviceToken] = useState('');
  const { register, handleSubmit, watch, setValue, formState: { errors }, reset } = useForm({
    resolver: yupResolver(loginSchema(t)),
    defaultValues: {
      emailOrPhone: "",
      password: "",
      country_code: "",
    },
  });
  const { loading } = useSelector((state) => state.login);

  const [showPassword, setShowPassword] = useState(false);
  const emailOrPhone = watch("emailOrPhone");
  const isEmailLogin = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOrPhone);
  const isPhoneLogin = /^[0-9]{8,15}$/.test(emailOrPhone);

  const { mobile_country_code } = useSelector((state) => state.internalUser);

  
  useEffect(() => {
    dispatch(fetchCountryMobileCode());
  }, [dispatch]);

  const onSubmit = async (data) => {
    try {
      const fcmToken = await requestFCMToken();
      if (fcmToken) setDeviceToken(fcmToken);
      const tokenToSend = fcmToken || deviceToken;

      let payload;
      const commonParams = {
        device_type: "web",
        ...(tokenToSend && { device_token: tokenToSend }),
      };
      if (!isPhoneLogin) {
        payload = { email: data.emailOrPhone, password: data.password, ...commonParams };
      } else {
        payload = { country_code: data.country_code, mobile_number: data.emailOrPhone, ...commonParams };
      }

      const response = await dispatch(doLogin(payload)).unwrap();

      if (response?.status) {
        if (!isPhoneLogin) {
          localStorage.setItem("token", response.data.auth_token);
          localStorage.setItem("user", JSON.stringify(response.data));
          localStorage.setItem("app_language", i18n.language);
          dispatch(setUser(response.data));
          Toaster.success(response.message || t("messages.loginSuccessful"));
          reset();
          if (isDeliveryPartner(response.data)) {
            navigate('/delivery-partner/new-deliveries');
          }else if (response?.data?.must_reset_password == 1) {
            navigate('/dashboard/profile/edit', {
              state: { active_tab: 'change_password' }
            });
          } else {
            navigate('/dashboard/home');
          }
        } else {
          dispatch(setLoginDetails(payload));
          reset();
         Toaster.success(t("messages.otpSentSuccessfully"));
          navigate('/verify-otp');
        }
      } else {
        Toaster.error(response.message || t("messages.somethingWentWrong"));
        dispatch(resetStatus());
        if (!isPhoneLogin) setValue("password", "");
      }
    } catch (error) {
      console.error('Login Error', error);
      Toaster.error(t("messages.unexpectedError"));
      dispatch(resetStatus());
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {loading && <ShowLoader />}
      <ScrollToTop />
      
      <Header/>

      <main
        className="relative flex-grow w-full flex items-center justify-center p-4"
        style={{ backgroundImage: "url('/img/bgImg.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black opacity-40 z-0"></div>

        <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-xl p-8">
          <Typography variant="h4" color="blue-gray" className="mb-6 text-left font-bold">
            {t("loginTitle")}
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">

            <div className="flex flex-col gap-2">
              {/* CHANGE: Label added here */}
              <Typography variant="h6" color="blue-gray" className="-mb-1">
                {t("emailOrPhoneLabel")}
              </Typography>
              <div className="flex items-start gap-2">
                {isPhoneLogin && (
                  <Select
                    placeholder={t("countryCode")}
                    {...register("country_code")}
                    onChange={(country_code) => {
                      setValue('country_code', country_code.value, { shouldValidate: true });
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        height: '44px',
                        minHeight: '44px',
                        borderColor: errors.country_code ? '#F44336' : '#B0BEC5',
                        boxShadow: state.isFocused ? '0 0 0 1px #333' : 'none',
                        '&:hover': { borderColor: '#90A4AE' }
                      }),
                    }}
                    className="min-w-[150px]"
                    options={mobile_country_code}
                    components={{
                        Option: CountryOption,
                        SingleValue: CountrySingleValue
                    }}
                  />
                )}
                <Input
                  type="text"
                  size="lg"
                  placeholder={t("emailOrPhonePlaceholder")}
                  {...register("emailOrPhone")}
                  error={Boolean(errors.emailOrPhone)}
                  className="!border-t-blue-gray-200 focus:!border-t-gray-900 w-full"
                  labelProps={{ className: "before:content-none after:content-none" }}
                />
              </div>
              {errors?.emailOrPhone && (
                <p className="text-red-500 text-sm ">{errors?.emailOrPhone?.message}</p>
              )}
              {errors?.country_code && (
                <p className="text-red-500 text-sm ">{errors?.country_code?.message}</p>
              )}
            </div>

            {/* CHANGE: Logic updated to `!isPhoneLogin` to show by default */}
            {!isPhoneLogin && (
              <div className="flex flex-col gap-2">
                {/* CHANGE: Label added here */}
                <Typography variant="h6" color="blue-gray" className="-mb-1">
                   {t("passwordLabel")}
                </Typography>
                <Input
                  type={showPassword ? "text" : "password"}
                  size="lg"
                  placeholder={t("passwordPlaceholder")}
                  {...register('password')}
                  error={Boolean(errors.password)}
                  className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{ className: "before:content-none after:content-none" }}
                  icon={
                    <div onClick={() => setShowPassword(!showPassword)} className="cursor-pointer">
                      {showPassword ? (
                        <EyeIcon className="h-5 w-5" />
                      ) : (
                        <EyeSlashIcon className="h-5 w-5" />
                      )}
                    </div>
                  }
                />
                {errors?.password && (
                  <p className="text-red-500 text-sm">{errors?.password?.message}</p>
                )}
              </div>
            )}

            {/* CHANGE: Logic updated to `!isPhoneLogin` */}
            {!isPhoneLogin && (
              <div className="flex justify-end -mt-3">
                <Link className="text-sm" to="/forgot-password"> {t("forgotPassword")}</Link>
                {/* <a href="#" className="text-sm font-medium text-blue-500 hover:underline ml-1">
                  Contact Admin
                </a> */}
              </div>
            )}

            <Button type="submit" fullWidth className="bg-black hover:bg-gray-800 text-base py-3 btn-material">
              {isPhoneLogin ? t("sendOtp") : t("loginButton")}
            </Button>

          </form>
        <div className="flex justify-center mt-6">
        <div className="text-sm text-gray-600 flex items-center gap-2">
         <span>{t("dontHaveAccount")}</span>

          <a
            href="/sign-up"
            className="flex items-center gap-1 font-semibold text-[#F2AD25] hover:text-[#d4961e] transition-all duration-200"
          >
            {t("requestDeliveryPartner")}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 transition-transform duration-200 hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>
            
        </div>

        <Typography variant="small" className="absolute bottom-1 mt-0 left-0 right-0 text-center text-white/80 z-10">
          {t("allRightsReserved")}
        </Typography>
      </main>
    </div>
  );
}

export default SignIn;